import type { Account } from "@/api/types";

/**
 * Which accounts a picker offers (ADR-0066, the chart-of-accounts handoff §4 rules 4 and 7, §6.6).
 *
 * Every picker leaves out inactive accounts, and the VAT accounts (1200, 2200) while the company isn't VAT
 * registered: they're seeded anyway, so registering later needs no chart change. A manual journal also leaves
 * out what only documents post to and the calculated 3200, and offers the VAT accounts only to an adjusting
 * entry. The database refuses all of these regardless (`ledger.assert_journal_can_use_account`); this keeps
 * the person from choosing one in the first place.
 *
 * A document's line (a bill, debit note, purchase order, invoice, credit note, quotation or billing item) names
 * only an account anything may post to. Every required account is left out: the control accounts (1100, 1150,
 * 1300, 2100, 2300) take the document's total, the tax accounts take what its tax code works out, and the rest
 * (salary payable, retained earnings, the calculated 3200, opening balance equity, exchange and rounding
 * differences) are posted by Rigo or by a journal. The owner ruled on 2026-11-05 that the pickers hide them.
 */

/** What anything may post to: an account with no posting rule of its own. */
const takesAnything = (account: Account) => (account.postingRule ?? "any") === "any";

/** VAT receivable and VAT payable: documents and the VAT return post to them. */
const isVatAccount = (account: Account) => account.postingRule === "documents_and_vat_return";

/** Active, and the VAT accounts only for a VAT-registered company: what any picker starts from. */
export function usableAccounts(
  accounts: readonly Account[] | undefined,
  { vatRegistered }: { vatRegistered: boolean },
): Account[] {
  return (accounts ?? []).filter(
    (account) => account.isActive && (vatRegistered || !isVatAccount(account)),
  );
}

/** Why a manual journal can't use this account, if it can't: the 422 code the API answers with. */
export function journalRefusal(
  account: Account,
  { adjusting }: { adjusting: boolean },
): "account_not_journal_postable" | "account_needs_adjusting_journal" | null {
  if (account.postingRule === "documents_only" || account.postingRule === "calculated") {
    return "account_not_journal_postable";
  }
  if (isVatAccount(account) && !adjusting) return "account_needs_adjusting_journal";
  return null;
}

/** What a manual journal line may name. */
export function journalAccounts(
  accounts: readonly Account[] | undefined,
  { vatRegistered, adjusting }: { vatRegistered: boolean; adjusting: boolean },
): Account[] {
  return usableAccounts(accounts, { vatRegistered }).filter(
    (account) => journalRefusal(account, { adjusting }) === null,
  );
}

/** What a document's line or a billing item may name: active accounts that take anything, never a required one. */
export function documentLineAccounts(accounts: readonly Account[] | undefined): Account[] {
  return (accounts ?? []).filter((account) => account.isActive && takesAnything(account));
}
